import React, { useState, useRef, useEffect } from 'react';
import { 
  Shield, AlertTriangle, Terminal, Globe, Server, Clock, ShieldCheck, 
  Link2Icon, MailIcon, Menu, ChevronRight, ExternalLink, Activity, 
  Database, Network, Lock, AlertOctagon, User, LogOut, Info, Flag, ShieldOff, Layers,
  BarChart, PieChart, TrendingUp, CheckCircle, XCircle, Search,  Settings, HelpCircle,
} from 'lucide-react';
import axios from 'axios';
import Avatar from '../../services/Avatar';

const AdvancedPhishingAnalyzer = () => {
  const [activeTab, setActiveTab] = useState('url');
  const [scanState, setScanState] = useState('idle'); // idle, scanning, complete
  const [inputUrl, setInputUrl] = useState(''); // Input for the 'url' tab
  const [inputTxt, setInputTxt] = useState(''); // Input for the 'txt' tab
  const [scanResults, setScanResults] = useState({});
  const [activeSection, setActiveSection] = useState('overview');
  const [analysisStatus, setAnalysisStatus] = useState('idle');
  const [displayResult, setDisplayResult] = useState('');
  const [borderColor, setBorderColor] = useState('border-gray-700');
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const timeoutRef = useRef(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const Skeleton = ({ width = "w-8", height = "h-8", rounded = "rounded-full" }) => (
    <div className={`${width} ${height} ${rounded} bg-gray-300 animate-pulse`} />
  );

   useEffect(() => {
          const controller = new AbortController();
          const fetchUserProfile = async () => {
              try {
                  const response = await axios.get('/users/me', { 
                      withCredentials: true,
                      signal: controller.signal // Attach signal to cancel duplicate requests
                  });
                  setCurrentUser(response.data);
              } catch (error) {
                  if (axios.isCancel(error)) {
                      //console.log('Request canceled:', error.message);
                  } else {
                      //console.error('Error fetching user profile:', error);
                  }
              }
          };
      
          fetchUserProfile();
      
          return () => controller.abort(); 
      }, []);
      
      const handleLogout = async () => {
        try {
          const response = await fetch('/d-auth/logout', {
            method: 'POST', // or 'GET', depending on your backend implementation
            credentials: 'include', // include cookies with the request
            headers: {
              'Content-Type': 'application/json',
            },
          });
      
          if (response.ok) {       
            
            window.location.href = '/'; 
          } else {
            // If there's an error during logout
            const errorData = await response.json();
            //console.error('Logout failed', errorData);
            alert( errorData ||'Logout failed. Please try again.');
          }
        } catch (error) {
          //console.error('Error during logout:', error);
          alert('An error occurred. Please try again.');
        }
      };

  const handleSubmitUrl = async (e) => {       
    e.preventDefault();    
    
    // Validation
    if (inputUrl.trim() === '') {
      alert('Please provide a URL.');
      return;
    }
    
    setScanState('scanning');

    try {
      const response = await axios.post('/v1/predict', { url: inputUrl });

      if (response.data.success) {
        // Destructuring with default values
        const {
          threat_level = 'Unknown',
          confidence = 'Unknown',
          detection_time = 'Unknown',
          phishing,
          indicators = 'N/A',
          dns_info = {}, // Capture the DNS info
          ssl_certificate = {}, // Capture the SSL info
          technologies = [], // Capture the Technologies
          redirection_chain = [], // Capture the Redirection URLs
          whois_info = {}  
        } = response.data;

        // Directly set scanResults 
        setScanResults({
          threatLevel: threat_level,
          confidence,
          detectionTime: detection_time,
          phishing,
          indicators,
          details: {
            dns: {
              ARecords: dns_info.A || [], // IP addresses from DNS A records
              MXRecords: dns_info.MX || [], // Mail exchange records
              NSRecords: dns_info.NS || [], // Name server records
              TXTRecords: dns_info.TXT || [], // Text records
              registrar: dns_info.registrar || 'Unknown', // Registrar information
              creationDate: dns_info.creationDate || 'Unknown', // Creation date
              expiryDate: dns_info.expiryDate || 'Unknown', // Expiry date
              ip: dns_info.A || [], // Store all IP addresses here
              location: ssl_certificate.issuer?.countryName || 'Unknown'
            },
            ssl: {
              issuer: ssl_certificate.issuer?.commonName || 'Unknown',
              validFrom: ssl_certificate.valid_from || 'Unknown',
              validTo: ssl_certificate.valid_until || 'Unknown',
              status: ssl_certificate.valid_from && ssl_certificate.valid_until ? 'Valid' : 'Invalid', // Example check
            },
            technologies: technologies || [],
            redirections: redirection_chain || [], 
            whois: whois_info  
          }
        });

        // Set scan state to complete after setting results
        setScanState('complete');      

      } else {
        alert(response.data.message || 'An error occurred during the analysis.');
        setScanState('idle');        
      }
    } catch (error) {
      //console.error("Error during API request:", error);
      alert('An unexpected error occurred.');
      setScanState('idle');     
    }
  };

  const handleSubmitTxt = async (e) => {
    e.preventDefault();

    // Validation
    if (inputTxt.trim() === '') {
      alert('Please provide a message.');
      return;
    }

    setAnalysisStatus('scanning');

    try {
      const response = await axios.post('/v1/analyze', { input: inputTxt });
      
      const result = response.data;
      const predictionLabel = result.result.label; 

      // Set borderColor based on the prediction
      if (predictionLabel === "Legitimate Email") {
        setBorderColor('border-green-600');
      } else if (predictionLabel === "Phishing Email") {
        setBorderColor('border-red-600');
      } else {
        setBorderColor('border-gray-700');  // Default color for neutral state
      }

      // Clear timeout if it's already set to prevent resetting too early
      clearTimeout(timeoutRef.current);

      // Reset border color after 15 seconds
      timeoutRef.current = setTimeout(() => {
        setBorderColor('border-gray-700'); // Reset to default color
      }, 15000);

      if (result.status === 'success') {
        // Access 'result' from the response directly
        const { result: predictionResult, time_taken } = result; // Destructure to obtain predictionResult

        setDisplayResult({
          ...predictionResult, // Spread the predictionResult object
          time_taken // Add time_taken to the result
        });
        
        setAnalysisStatus('complete'); // Update the scan state
      } else {
        alert(`Error: ${result.message}`);
        setAnalysisStatus('idle');
      }
    } catch (error) {
      alert(`Failed to analyze text: ${error.message}`);
      setAnalysisStatus('idle');
    }
  };

  // Function to get color intensity for gauge chart
  const getGaugePathColor = (value) => {
    if (value < 30) return "#10B981"; // green
    if (value < 70) return "#FBBF24"; // yellow
    return "#EF4444"; // red
  };

  // Function to convert percentage to SVG arc path
  const percentageToSVGArc = (percentage, radius) => {
    const angleInRadians = ((percentage / 100) * 180 * Math.PI) / 180;
    const x = radius * Math.sin(angleInRadians);
    const y = -radius * Math.cos(angleInRadians);
    const largeArcFlag = percentage > 50 ? 1 : 0;
    return `M 0 -${radius} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x} ${y}`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-900 text-gray-100">
      {/* Top Navigation */}
      <nav className="fixed top-0 left-0 w-full bg-gray-900 border-b border-gray-800 p-4 shadow-lg z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Shield className="text-red-500" size={28} />
              <span className="text-xl font-bold bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
                PhishGuard Pro
              </span>
              <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded-full">v2.1</span>
            </div>
          </div>

          <div className="flex items-center space-x-6">
            <div className="hidden md:flex items-center space-x-6">
              <button 
                onClick={() => setActiveTab('url')}
                className={`flex items-center space-x-2 transition-colors ${activeTab === 'url' ? 'text-red-500' : 'text-gray-400 hover:text-white'}`}
              >
                <Link2Icon size={18} />
                <span>URL Analysis</span>
              </button>
              <button 
                onClick={() => setActiveTab('email')}
                className={`flex items-center space-x-2 transition-colors ${activeTab === 'email' ? 'text-red-500' : 'text-gray-400 hover:text-white'}`}
              >
                <MailIcon size={18} />
                <span>Email Analysis</span>
              </button>
              <a href="#" className="text-gray-400 hover:text-white transition-colors flex items-center space-x-2">
                <Settings size={18} />
                <span>Settings</span>
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors flex items-center space-x-2">
                <HelpCircle size={18} />
                <span>Help</span>
              </a>
            </div>

            <div className="relative">
              <button 
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="flex items-center space-x-2 focus:outline-none"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-500 to-red-700 flex items-center justify-center">
                  
                                    {currentUser ? (
                                        <>
                                          <Avatar firstName={currentUser.first_name} lastName={currentUser.last_name} />
                                        </>
                                      ) : (
                                        <div className="flex items-center gap-2">
                                          <Skeleton width="w-10 h-10" rounded="rounded-full" /> {/* Skeleton for Avatar */}
                                          <div className="flex flex-col">
                                            <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                                            <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                                          </div>
                                        </div>
                                      )}
                </div>
                <ChevronRight size={16} className={`transition-transform ${showProfileDropdown ? 'rotate-90' : ''}`} />
              </button>

              {showProfileDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-700">
                  <a href="#" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center space-x-2">
                    <User size={16} />
                    <span>Profile</span>
                  </a>
                  <a href="#" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center space-x-2">
                    <Settings size={16} />
                    <span>Settings</span>
                  </a>
                  <div className="border-t border-gray-700 my-1"></div>
                  <button
                    onClick={handleLogout}
                    className="block px-4 py-2 text-sm text-red-400 hover:bg-gray-700 flex items-center space-x-2">
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden pt-20"> 
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Tab Navigation */}
          <div className="mb-6 bg-gray-950 rounded-lg p-2 border border-gray-800 flex space-x-2">
            <button
              onClick={() => setActiveTab('url')}
              className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg transition-all ${
                activeTab === 'url' 
                  ? 'bg-red-900/30 text-red-400' 
                  : 'hover:bg-gray-800 text-gray-400'
              }`}
            >
              <Link2Icon size={18} />
              <span className="font-medium">URL Analysis</span>
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg transition-all ${
                activeTab === 'email' 
                  ? 'bg-red-900/30 text-red-400' 
                  : 'hover:bg-gray-800 text-gray-400'
              }`}
            >
              <MailIcon size={18} />
              <span className="font-medium">Email Analysis</span>
            </button>
          </div>

          {/* Content Area */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Analysis Input */}
            <div className="lg:col-span-4">
              <div className="bg-gray-950 rounded-lg p-6 shadow-lg border border-gray-800 h-full">
                <div className="flex items-center space-x-2 mb-6">
                  <Terminal size={20} className="text-red-500" />
                  <h2 className="text-xl font-bold">Advanced {activeTab.toUpperCase()} Analysis</h2>
                </div>

                {activeTab === 'url' ? (
                  <form onSubmit={handleSubmitUrl} className="space-y-4">
                    <div className="relative">
                      <input
                        type="url"
                        className="w-full p-4 bg-gray-900 rounded-lg border border-gray-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-colors pl-12"
                        placeholder="Enter URL for analysis..."
                        value={inputUrl}
                        onChange={(e) => setInputUrl(e.target.value)}
                      />
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                        <Globe size={20} className="text-gray-500" />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={scanState === 'scanning'}
                      className={`w-full py-4 px-6 rounded-lg font-medium transition-all ${
                        scanState === 'scanning'
                          ? 'bg-gradient-to-r from-red-600 to-red-800 animate-pulse'
                          : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600'
                      }`}
                    >
                      {scanState === 'scanning' ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                          <span>Analyzing Target...</span>
                        </div>
                      ) : (
                        'Initialize Security Scan'
                      )}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleSubmitTxt} className="space-y-4">
                    <textarea
                      className={`w-full p-4 bg-gray-900 rounded-lg border ${borderColor} focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-colors resize-none`}
                      placeholder="Paste email content..."
                      value={inputTxt}
                      onChange={(e) => setInputTxt(e.target.value)}
                      rows={6}
                    />
                    <button
                      type="submit"
                      disabled={analysisStatus === 'scanning'}
                      className={`w-full py-4 px-6 rounded-lg font-medium transition-all ${
                        analysisStatus === 'scanning'
                          ? 'bg-gradient-to-r from-red-600 to-red-800 animate-pulse'
                          : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600'
                      }`}
                    >
                      {analysisStatus === 'scanning' ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                          <span>Analyzing Target...</span>
                        </div>
                      ) : (
                        'Analyze Email'
                      )}
                    </button>
                  </form>
                )}

                {/* Add analysis tips */}
                <div className="mt-6 p-4 bg-gray-900 rounded-lg border border-gray-800">
                  <h3 className="text-sm font-semibold text-gray-300 flex items-center space-x-2 mb-2">
                    <Info size={16} className="text-blue-400" />
                    <span>Analysis Tips</span>
                  </h3>
                  <ul className="text-xs text-gray-400 space-y-2">
                    <li className="flex items-start space-x-2">
                      <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Submit complete URLs including the protocol (http:// or https://)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span>For email analysis, include headers for more accurate results</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <XCircle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                      <span>Avoid submitting personal or sensitive information</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Results Area */}
            <div className="lg:col-span-8">
              {/* Scanning Animation for Email */}
              {analysisStatus === 'scanning' && activeTab === 'email' && (
                <div className="bg-gray-950 rounded-lg p-6 border border-gray-800 shadow-lg">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Activity className="text-red-500 animate-pulse" size={24} />
                        <h3 className="text-lg font-bold">Email Analysis in Progress</h3>
                      </div>
                      <span className="text-red-500 animate-pulse">Processing</span>
                    </div>
                    
                    <div className="w-full bg-gray-800 h-2 rounded-full overflow-hidden">
                      <div className="bg-gradient-to-r from-red-600 to-red-400 h-full animate-pulse rounded-full" style={{ width: '70%' }}></div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {['Checking Message Headers', 'Email Content Analysis', 'Link Analysis', 'IP Address Verification', 'Threat Detection', 'Sender Validation'].map((step, index) => (
                        <div key={step} className="flex items-center space-x-3 bg-gray-900 p-3 rounded-lg">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <div className={`animate-spin rounded-full h-4 w-4 border-2 ${
                              index % 2 === 0 ? 'border-red-500' : 'border-blue-500'
                            } border-t-transparent`}></div>
                          </div>
                          <span className="text-sm">{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Scanning Animation for URL */}
              {scanState === 'scanning' && activeTab === 'url' && (
                <div className="bg-gray-950 rounded-lg p-6 border border-gray-800 shadow-lg">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Activity className="text-red-500 animate-pulse" size={24} />
                        <h3 className="text-lg font-bold">URL Analysis in Progress</h3>
                      </div>
                      <span className="text-red-500 animate-pulse">Processing</span>
                    </div>
                    
                    <div className="w-full bg-gray-800 h-2 rounded-full overflow-hidden">
                      <div className="bg-gradient-to-r from-red-600 to-red-400 h-full animate-pulse rounded-full" style={{ width: '70%' }}></div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {['Initializing Scan', 'DNS Analysis', 'SSL Verification', 'Content Analysis', 'Threat Detection', 'Reputation Check'].map((step, index) => (
                        <div key={step} className="flex items-center space-x-3 bg-gray-900 p-3 rounded-lg">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <div className={`animate-spin rounded-full h-4 w-4 border-2 ${
                              index % 2 === 0 ? 'border-red-500' : 'border-blue-500'
                            } border-t-transparent`}></div>
                          </div>
                          <span className="text-sm">{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Email Analysis Results */}
              {analysisStatus === 'complete' && displayResult && activeTab === 'email' && (
                <div className="space-y-6">
                  {/* Threat Summary Card */}
                  <div className="bg-gray-950 rounded-lg p-6 border border-gray-800 shadow-lg">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-bold flex items-center space-x-2">
                        <AlertTriangle className={`${
                          displayResult.label === 'Phishing Email' ? 'text-red-500' : 'text-green-500'
                        }`} />
                        <span>Email Analysis Results</span>
                      </h3>
                      <div className="flex items-center space-x-2">
                        <Clock size={16} className="text-gray-400" />
                        <span className="text-sm text-gray-400">Analyzed in {displayResult.time_taken}</span>
                      </div>
                    </div>

                    {/* Visual gauge chart */}
                    <div className="flex justify-center mb-6">
                      <div className="relative w-64 h-32">
                        {/* Background arc */}
                        <svg className="w-full h-full" viewBox="-100 -100 200 100">
                          <path
                            d="M -80 0 A 80 80 0 1 1 80 0"
                            fill="none"
                            stroke="#374151"
                            strokeWidth="12"
                            strokeLinecap="round"
                          />
                          {/* Foreground arc */}
                          <path
                            d={percentageToSVGArc(displayResult.confidence, 80)}
                            fill="none"
                            stroke={getGaugePathColor(displayResult.confidence)}
                            strokeWidth="12"
                            strokeLinecap="round"
                          />
                          {/* Needle */}
                          <line
                            x1="0"
                            y1="0"
                            x2={80 * Math.sin((displayResult.confidence / 100) * Math.PI)}
                            y2={-80 * Math.cos((displayResult.confidence / 100) * Math.PI)}
                            stroke="#F9FAFB"
                            strokeWidth="2"
                          />
                          <circle cx="0" cy="0" r="6" fill="#F9FAFB" />
                          {/* Text */}
                          <text
                            x="0"
                            y="30"
                            textAnchor="middle"
                            fill="#F9FAFB"
                            fontSize="24"
                            fontWeight="bold"
                          >
                            {displayResult.confidence.toFixed(1)}%
                          </text>
                        </svg>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Prediction Label */}
                      <div className={`bg-gray-900 p-4 rounded-lg border ${
                        displayResult.label === 'Phishing Email' ? 'border-red-600 bg-red-900/10' : 'border-green-600 bg-green-900/10'
                      }`}>
                        <div className="flex items-center space-x-2 mb-3">
                          <AlertOctagon className={displayResult.label === 'Phishing Email' ? 'text-red-500' : 'text-green-500'} />
                          <span className="font-medium">Classification</span>
                        </div>
                        <div className={`text-xl font-bold ${
                          displayResult.label === 'Phishing Email' ? 'text-red-500' : 'text-green-500'
                        }`}>
                          {displayResult.label}
                        </div>
                      </div>

                      {/* Danger Level */}
                      <div className={`bg-gray-900 p-4 rounded-lg border ${
                        displayResult.danger_level === 'Critical' ? 'border-red-600 bg-red-900/10' : 
                        displayResult.danger_level === 'High' ? 'border-orange-600 bg-orange-900/10' : 
                        'border-green-600 bg-green-900/10'
                      }`}>
                        <div className="flex items-center space-x-2 mb-3">
                          <ShieldCheck className={
                            displayResult.danger_level === 'Critical' ? 'text-red-500' : 
                            displayResult.danger_level === 'High' ? 'text-orange-500' : 
                            'text-green-500'
                          } />
                          <span className="font-medium">Threat Level</span>
                        </div>
                        <div className={`text-xl font-bold ${
                          displayResult.danger_level === 'Critical' ? 'text-red-500' : 
                          displayResult.danger_level === 'High' ? 'text-orange-500' : 
                          'text-green-500'
                        }`}>
                          {displayResult.danger_level}
                        </div>
                      </div>

                      {/* Time Taken */}
                      <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                        <div className="flex items-center space-x-2 mb-3">
                          <Clock className="text-blue-500" />
                          <span className="font-medium">Analysis Time</span>
                        </div>
                        <div className="text-xl font-bold text-blue-400">
                          {displayResult.time_taken}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Detailed Analysis */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Email Characteristics */}
                    <div className="bg-gray-950 rounded-lg p-6 border border-gray-800 shadow-lg">
                      <h3 className="text-lg font-bold mb-4">Email Characteristics</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-gray-900 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <User size={16} className="text-blue-400" />
                            <span>Sender Analysis</span>
                          </div>
                          <span className={displayResult.label === 'Phishing Email' ? 'text-red-500' : 'text-green-500'}>
                            {displayResult.label === 'Phishing Email' ? 'Suspicious' : 'Legitimate'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-900 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <Link2Icon size={16} className="text-blue-400" />
                            <span>Links</span>
                          </div>
                          <span className={displayResult.label === 'Phishing Email' ? 'text-red-500' : 'text-green-500'}>
                            {displayResult.label === 'Phishing Email' ? 'Potentially Malicious' : 'Clean'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-900 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <Lock size={16} className="text-blue-400" />
                            <span>Security Indicators</span>
                          </div>
                          <span className={displayResult.label === 'Phishing Email' ? 'text-red-500' : 'text-green-500'}>
                            {displayResult.label === 'Phishing Email' ? 'Missing' : 'Present'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-900 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <AlertTriangle size={16} className="text-blue-400" />
                            <span>Urgency Level</span>
                          </div>
                          <span className={displayResult.label === 'Phishing Email' ? 'text-red-500' : 'text-green-500'}>
                            {displayResult.label === 'Phishing Email' ? 'High' : 'Normal'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Risk Visualization */}
                    <div className="bg-gray-950 rounded-lg p-6 border border-gray-800 shadow-lg">
                      <h3 className="text-lg font-bold mb-4">Risk Assessment</h3>
                      
                      {/* Simulated radar chart */}
                      <div className="flex justify-center mb-4 p-4">
                        <svg width="180" height="180" viewBox="0 0 200 200">
                          {/* Background pentagon */}
                          <path d="M100,10 L180,75 L150,180 L50,180 L20,75 Z" fill="none" stroke="#374151" strokeWidth="1" />
                          <path d="M100,40 L152,85 L130,155 L70,155 L48,85 Z" fill="none" stroke="#374151" strokeWidth="1" />
                          <path d="M100,70 L125,95 L110,130 L90,130 L75,95 Z" fill="none" stroke="#374151" strokeWidth="1" />
                          
                          {/* Data pentagon - will vary based on threat level */}
                          <path 
                            d={displayResult.label === 'Phishing Email' 
                              ? "M100,20 L165,78 L140,170 L60,170 L35,78 Z" 
                              : "M100,55 L130,90 L115,140 L85,140 L70,90 Z"
                            } 
                            fill={displayResult.label === 'Phishing Email' ? "rgba(239, 68, 68, 0.2)" : "rgba(16, 185, 129, 0.2)"}
                            stroke={displayResult.label === 'Phishing Email' ? "#EF4444" : "#10B981"} 
                            strokeWidth="2"
                          />
                          
                          {/* Labels */}
                          <text x="100" y="15" textAnchor="middle" fill="#9CA3AF" fontSize="10">Content</text>
                          <text x="185" y="80" textAnchor="end" fill="#9CA3AF" fontSize="10">Links</text>
                          <text x="155" y="185" textAnchor="middle" fill="#9CA3AF" fontSize="10">Headers</text>
                          <text x="45" y="185" textAnchor="middle" fill="#9CA3AF" fontSize="10">Sender</text>
                          <text x="15" y="80" textAnchor="start" fill="#9CA3AF" fontSize="10">Attachments</text>
                        </svg>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Content Risk</span>
                          <div className="w-32 bg-gray-800 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                displayResult.label === 'Phishing Email' ? 'bg-red-500' : 'bg-green-500'
                              }`} 
                              style={{ width: `${displayResult.label === 'Phishing Email' ? '85%' : '25%'}` }}
                            ></div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Link Risk</span>
                          <div className="w-32 bg-gray-800 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                displayResult.label === 'Phishing Email' ? 'bg-red-500' : 'bg-green-500'
                              }`} 
                              style={{ width: `${displayResult.label === 'Phishing Email' ? '95%' : '15%'}` }}
                            ></div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Sender Risk</span>
                          <div className="w-32 bg-gray-800 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                displayResult.label === 'Phishing Email' ? 'bg-red-500' : 'bg-green-500'
                              }`} 
                              style={{ width: `${displayResult.label === 'Phishing Email' ? '75%' : '10%'}` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Recommendations */}
                  <div className="bg-gray-950 rounded-lg p-6 border border-gray-800 shadow-lg">
                    <h3 className="text-lg font-bold mb-4">Recommendations</h3>
                    <div className="space-y-3">
                      {displayResult.label === 'Phishing Email' ? (
                        <>
                          <div className="flex items-start space-x-3 p-3 bg-red-900/10 rounded-lg">
                            <XCircle className="text-red-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="font-medium">Do Not Engage</h4>
                              <p className="text-sm text-gray-400">Avoid clicking any links or downloading attachments from this email.</p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-3 p-3 bg-yellow-900/10 rounded-lg">
                            <AlertTriangle className="text-yellow-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="font-medium">Report to IT</h4>
                              <p className="text-sm text-gray-400">Forward this email to your IT security team for further investigation.</p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-3 p-3 bg-blue-900/10 rounded-lg">
                            <ShieldCheck className="text-blue-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="font-medium">Verify Sender</h4>
                              <p className="text-sm text-gray-400">Contact the supposed sender through a verified channel to confirm legitimacy.</p>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-start space-x-3 p-3 bg-green-900/10 rounded-lg">
                            <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="font-medium">Safe to Proceed</h4>
                              <p className="text-sm text-gray-400">This email appears legitimate. Still exercise caution with any links or attachments.</p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-3 p-3 bg-blue-900/10 rounded-lg">
                            <Info className="text-blue-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="font-medium">Check Links</h4>
                              <p className="text-sm text-gray-400">Hover over links to verify they lead to expected destinations before clicking.</p>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* URL Analysis Results */}
              {scanState === 'complete' && scanResults && activeTab === 'url' && (
                <div className="space-y-6">
                  {/* Threat Summary Card */}
                  <div className="bg-gray-950 rounded-lg p-6 border border-gray-800 shadow-lg">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-bold flex items-center space-x-2">
                        <ShieldCheck className={`${
                          scanResults.phishing ? 'text-red-500' : 'text-green-500'
                        }`} />
                        <span>URL Analysis Results</span>
                      </h3>
                      <div className="flex items-center space-x-2">
                        <Clock size={16} className="text-gray-400" />
                        <span className="text-sm text-gray-400">Analyzed in {scanResults.detectionTime}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Threat Level */}
                      <div className={`bg-gray-900 p-4 rounded-lg border ${
                        scanResults.threatLevel === 'High' ? 'border-red-600 bg-red-900/10' : 
                        scanResults.threatLevel === 'Medium' ? 'border-yellow-600 bg-yellow-900/10' : 
                        'border-green-600 bg-green-900/10'
                      }`}>
                        <div className="flex items-center space-x-2 mb-3">
                          <AlertOctagon className={
                            scanResults.threatLevel === 'High' ? 'text-red-500' : 
                            scanResults.threatLevel === 'Medium' ? 'text-yellow-500' : 
                            'text-green-500'
                          } />
                          <span className="font-medium">Threat Level</span>
                        </div>
                        <div className={`text-xl font-bold ${
                          scanResults.threatLevel === 'High' ? 'text-red-500' : 
                          scanResults.threatLevel === 'Medium' ? 'text-yellow-500' : 
                          'text-green-500'
                        }`}>
                          {scanResults.threatLevel}
                        </div>
                      </div>

                      {/* Phishing Status */}
                      <div className={`bg-gray-900 p-4 rounded-lg border ${
                        scanResults.phishing ? 'border-red-600 bg-red-900/10' : 'border-green-600 bg-green-900/10'
                      }`}>
                        <div className="flex items-center space-x-2 mb-3">
                          <ShieldCheck className={scanResults.phishing ? 'text-red-500' : 'text-green-500'} />
                          <span className="font-medium">Phishing Status</span>
                        </div>
                        <div className={`text-xl font-bold ${
                          scanResults.phishing ? 'text-red-500' : 'text-green-500'
                        }`}>
                          {scanResults.phishing ? 'Phishing Detected' : 'Safe'}
                        </div>
                      </div>

                      {/* Confidence */}
                      <div className={`bg-gray-900 p-4 rounded-lg border ${
                        parseFloat(scanResults.confidence) > 75 ? 'border-green-600 bg-green-900/10' : 
                        parseFloat(scanResults.confidence) > 50 ? 'border-yellow-600 bg-yellow-900/10' : 
                        'border-red-600 bg-red-900/10'
                      }`}>
                        <div className="flex items-center space-x-2 mb-3">
                          <Database className={
                            parseFloat(scanResults.confidence) > 75 ? 'text-green-500' : 
                            parseFloat(scanResults.confidence) > 50 ? 'text-yellow-500' : 
                            'text-red-500'
                          } />
                          <span className="font-medium">Confidence</span>
                        </div>
                        <div className={`text-xl font-bold ${
                          parseFloat(scanResults.confidence) > 75 ? 'text-green-500' : 
                          parseFloat(scanResults.confidence) > 50 ? 'text-yellow-500' : 
                          'text-red-500'
                        }`}>
                          {scanResults.confidence}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Technical Analysis */}
                  <div className="bg-gray-950 rounded-lg p-6 border border-gray-800 shadow-lg">
                    <h3 className="text-lg font-bold mb-6 flex items-center space-x-2">
                      <Terminal className="text-blue-500" />
                      <span>Technical Analysis</span>
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* DNS Information */}
                      <div className="bg-gray-900 p-5 rounded-lg border border-gray-700">
                        <h4 className="font-medium mb-4 flex items-center space-x-2">
                          <Globe className="text-green-500" />
                          <span>DNS Information</span>
                        </h4>
                        <div className="space-y-4 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Registrar:</span>
                            <span>{scanResults.details.whois?.registrar || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Creation Date:</span>
                            <span>{scanResults.details.whois?.creation_date?.[0] || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Expiry Date:</span>
                            <span>{scanResults.details.whois?.expiration_date?.[0] || 'N/A'}</span>
                          </div>
                          <div>
                            <div className="text-gray-400 mb-2">IP Addresses:</div>
                            <div className="bg-gray-800/50 p-3 rounded-lg">
                              {scanResults.details.dns.ip.map((ip, index) => (
                                <div key={index} className="flex items-center space-x-2 py-1">
                                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                  <span className="font-mono text-sm">{ip}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* SSL Certificate */}
                      <div className="bg-gray-900 p-5 rounded-lg border border-gray-700">
                        <h4 className="font-medium mb-4 flex items-center space-x-2">
                          <Lock className="text-yellow-500" />
                          <span>SSL Certificate</span>
                        </h4>
                        <div className="space-y-4 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Issuer:</span>
                            <span>{scanResults.details.ssl.issuer}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Valid From:</span>
                            <span>{scanResults.details.ssl.validFrom}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Valid To:</span>
                            <span>{scanResults.details.ssl.validTo}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Status:</span>
                            <span className={scanResults.details.ssl.status === 'Valid' ? 'text-green-400' : 'text-red-400'}>
                              {scanResults.details.ssl.status}
                            </span>
                          </div>
                        </div>
                      </div>

                     

                      {/* Redirection Chain */}
                      <div className="bg-gray-900 p-5 rounded-lg border border-gray-700 md:col-span-2">
                        <h4 className="font-medium mb-4 flex items-center space-x-2">
                          <Flag className="text-red-500" />
                          <span>Redirection Chain</span>
                        </h4>
                        <div className="space-y-3">
                          {scanResults.details.redirections.map((url, index) => (
                            <div key={url} className="flex items-start space-x-3">
                              <div className="flex flex-col items-center pt-1">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                                  index === scanResults.details.redirections.length - 1 ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'
                                }`}>
                                  {index === scanResults.details.redirections.length - 1 ? (
                                    <ShieldOff size={12} />
                                  ) : (
                                    <ChevronRight size={12} />
                                  )}
                                </div>
                                {index < scanResults.details.redirections.length - 1 && (
                                  <div className="w-0.5 h-6 bg-gray-700 my-1"></div>
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="text-sm break-all">{url}</div>
                                {index === scanResults.details.redirections.length - 1 && (
                                  <div className="text-xs text-red-400 mt-1">Final Destination</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                   {/* WHOIS Information Card - Add this to the URL Analysis Results section */}
                   <div className="bg-gray-950 rounded-lg p-6 border border-gray-800 shadow-lg">
                      <h3 className="text-lg font-bold mb-6 flex items-center space-x-2">
                        <Globe className="text-blue-500" />
                        <span>WHOIS Information</span>
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Left Column */}
                        <div className="space-y-4">
                          <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                            <h4 className="font-medium mb-3 flex items-center space-x-2">
                              <Database className="text-green-500" size={16} />
                              <span>Domain Details</span>
                            </h4>
                            <div className="space-y-3 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-400">Domain Name:</span>
                                <span className="font-mono">{scanResults.details.whois?.domain_name || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Registrar:</span>
                                <span>{scanResults.details.whois?.registrar || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Registration Date:</span>
                                <span>{scanResults.details.whois?.creation_date?.[0] || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Expiration Date:</span>
                                <span>{scanResults.details.whois?.expiration_date?.[0] || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Last Updated:</span>
                                <span>{scanResults.details.whois?.updated_date?.[0] || 'N/A'}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                            <h4 className="font-medium mb-3 flex items-center space-x-2">
                              <Server className="text-yellow-500" size={16} />
                              <span>Technical Details</span>
                            </h4>
                            <div className="space-y-3 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-400">DNSSEC:</span>
                                <span className={`${
                                  scanResults.details.whois?.dnssec === 'signed' ? 'text-green-500' : 'text-yellow-500'
                                }`}>
                                  {scanResults.details.whois?.dnssec || 'N/A'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Status:</span>
                                <div className="flex flex-wrap justify-end gap-1">
                                  {Array.isArray(scanResults.details.whois?.status) 
                                    ? scanResults.details.whois.status.map((status, i) => (
                                        <span key={i} className="text-xs bg-gray-800 px-2 py-1 rounded">
                                          {status.split(' ')[0]}
                                        </span>
                                      ))
                                    : scanResults.details.whois?.status?.split(' ').map((status, i) => (
                                        <span key={i} className="text-xs bg-gray-800 px-2 py-1 rounded">
                                          {status}
                                        </span>
                                      )) || 'N/A'
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Right Column */}
                        <div className="space-y-4">
                          <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                            <h4 className="font-medium mb-3 flex items-center space-x-2">
                              <User className="text-purple-500" size={16} />
                              <span>Registrant Information</span>
                            </h4>
                            <div className="space-y-3 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-400">Country:</span>
                                <span className="flex items-center">
                                  {scanResults.details.whois?.country || 'N/A'}
                                  {scanResults.details.whois?.country && (
                                    <span className="ml-2 text-xs bg-gray-800 px-2 py-0.5 rounded">
                                      {scanResults.details.whois.country}
                                    </span>
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Organization:</span>
                                <span>{scanResults.details.whois?.org || 'REDACTED'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Contact Email:</span>
                                <span className="text-blue-400 break-all">
                                  {scanResults.details.whois?.emails || 'REDACTED'}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                            <h4 className="font-medium mb-3 flex items-center space-x-2">
                              <Network className="text-red-500" size={16} />
                              <span>Name Servers</span>
                            </h4>
                            <div className="space-y-2 text-sm">
                              {scanResults.details.whois?.name_servers?.length ? (
                                scanResults.details.whois.name_servers.map((ns, i) => (
                                  <div key={i} className="flex items-center space-x-2 p-2 bg-gray-800/50 rounded">
                                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                    <span className="font-mono">{ns}</span>
                                  </div>
                                ))
                              ) : (
                                <div className="text-gray-500">No name servers information available</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Registrar Information */}
                      <div className="mt-4 bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <ExternalLink size={16} className="text-blue-500" />
                            <span className="text-sm">Registrar URL:</span>
                          </div>
                          {scanResults.details.whois?.registrar_url ? (
                            <a 
                              href={`https://${scanResults.details.whois.registrar_url}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:underline text-sm"
                            >
                              {scanResults.details.whois.registrar_url}
                            </a>
                          ) : (
                            <span className="text-gray-500 text-sm">N/A</span>
                          )}
                        </div>
                      </div>
                    </div>

                  {/* Technologies */}
                  <div className="bg-gray-950 rounded-lg p-6 border border-gray-800 shadow-lg">
                    <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                      <Layers className="text-purple-500" />
                      <span>Detected Technologies</span>
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {scanResults.details.technologies.map((tech, index) => (
                        <span key={tech} className="text-xs bg-gray-800 px-3 py-1 rounded-full border border-gray-700">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

       {/* Footer */}
       <footer className="bg-gray-900 border-t border-gray-800 py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <Shield className="text-red-500" size={20} />
              <span className="font-medium">PhishGuard Pro</span>
              <span className="text-xs bg-gray-800 text-gray-400 px-2 py-1 rounded-full">v2.1</span>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-400">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors">API Documentation</a>
              <a href="#" className="hover:text-white transition-colors">Contact Support</a>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-gray-800 text-center text-xs text-gray-500">
            <p>Developed by F/N Nuru The_Geek. • Powered by Machine Learning and Threat Intelligence</p>
            <p className="mt-2">© {new Date().getFullYear()} All rights reserved</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdvancedPhishingAnalyzer;
